import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Search, User, Mail, CheckSquare, DollarSign, Sparkles, Loader2, X } from 'lucide-react';
import { SidebarItem } from '../types/cockpit';
import { EnhancedDashboardTask, DashboardEmail, DashboardClient, PlannerTask } from '../types/api';
import LexAIService from '../services/api';

/**
 * Represents a search result item with type information
 */
export interface SearchResult {
  id: string;
  type: 'email' | 'task' | 'client' | 'deal';
  title: string;
  subtitle: string;
  data?: any;
}

/**
 * Grouped search results by type
 */
interface GroupedResults {
  clients: SearchResult[];
  deals: SearchResult[];
  tasks: SearchResult[];
  emails: SearchResult[];
}

/**
 * Props for the SearchResults component
 */
interface SearchResultsProps {
  /** The current search query string */
  query: string;
  /** Callback when search query changes */
  onQueryChange: (query: string) => void;
  /** Callback when search is closed */
  onClose: () => void;
  /** Available sidebar data for client-side filtering */
  sidebarData: {
    clients: SidebarItem[];
    deals: SidebarItem[];
    tasks: SidebarItem[];
    emails: SidebarItem[];
  };
  /** Full task data for enhanced search */
  fullTasksData: EnhancedDashboardTask[];
  /** Full email data for enhanced search */
  fullEmailsData: DashboardEmail[];
  /** Callback when a result is selected */
  onResultSelect: (result: SearchResult) => void;
  /** Placeholder text for the search input */
  placeholder?: string;
}

/**
 * SearchResults component provides a search dropdown with client-side filtering
 * and optional AI-powered search functionality
 */
const SearchResults: React.FC<SearchResultsProps> = ({
  query,
  onQueryChange,
  onClose,
  sidebarData,
  fullTasksData,
  fullEmailsData,
  onResultSelect,
  placeholder = "Search or ask Lex AI..."
}) => {
  const [isAISearching, setIsAISearching] = useState(false);
  const [aiResponse, setAIResponse] = useState<string | null>(null);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Minimum characters before showing results
  const MIN_SEARCH_LENGTH = 2;

  /**
   * Filter sidebar items based on search query
   */
  const filterItems = useCallback((items: SidebarItem[], searchQuery: string): SearchResult[] => {
    const lowerQuery = searchQuery.toLowerCase();
    return items
      .filter(item =>
        item.title.toLowerCase().includes(lowerQuery) ||
        (item.subtitle && item.subtitle.toLowerCase().includes(lowerQuery))
      )
      .slice(0, 5) // Limit to 5 results per category
      .map(item => ({
        id: item.id,
        type: item.type as 'email' | 'task' | 'client' | 'deal',
        title: item.title,
        subtitle: item.subtitle || '',
        data: item
      }));
  }, []);

  /**
   * Enhanced email filtering with sender email
   */
  const filterEmails = useCallback((searchQuery: string): SearchResult[] => {
    const lowerQuery = searchQuery.toLowerCase();
    return fullEmailsData
      .filter(email =>
        email.subject.toLowerCase().includes(lowerQuery) ||
        email.sender.toLowerCase().includes(lowerQuery) ||
        (email.senderEmail && email.senderEmail.toLowerCase().includes(lowerQuery)) ||
        (email.bodyPreview && email.bodyPreview.toLowerCase().includes(lowerQuery))
      )
      .slice(0, 5)
      .map((email, index) => ({
        id: email.id || `email-${index}`,
        type: 'email' as const,
        title: email.subject,
        subtitle: `from ${email.sender}`,
        data: email
      }));
  }, [fullEmailsData]);

  /**
   * Enhanced task filtering with description
   */
  const filterTasks = useCallback((searchQuery: string): SearchResult[] => {
    const lowerQuery = searchQuery.toLowerCase();
    return fullTasksData
      .filter(task =>
        task.task.toLowerCase().includes(lowerQuery) ||
        (task.description && task.description.toLowerCase().includes(lowerQuery)) ||
        (task.clientName && task.clientName.toLowerCase().includes(lowerQuery)) ||
        task.assignedTo.toLowerCase().includes(lowerQuery)
      )
      .slice(0, 5)
      .map(task => ({
        id: task.id,
        type: 'task' as const,
        title: task.task,
        subtitle: task.status,
        data: task
      }));
  }, [fullTasksData]);

  /**
   * Compute grouped search results
   */
  const groupedResults: GroupedResults = useMemo(() => {
    if (query.length < MIN_SEARCH_LENGTH) {
      return { clients: [], deals: [], tasks: [], emails: [] };
    }

    return {
      clients: filterItems(sidebarData.clients, query).map(r => ({ ...r, type: 'client' as const })),
      deals: filterItems(sidebarData.deals, query).map(r => ({ ...r, type: 'deal' as const })),
      tasks: filterTasks(query),
      emails: filterEmails(query)
    };
  }, [query, sidebarData, filterItems, filterTasks, filterEmails]);

  /**
   * Flatten results for keyboard navigation
   */
  const flatResults = useMemo(() => {
    return [
      ...groupedResults.clients,
      ...groupedResults.deals,
      ...groupedResults.tasks,
      ...groupedResults.emails
    ];
  }, [groupedResults]);

  /**
   * Total result count
   */
  const totalResults = flatResults.length;

  /**
   * Check if there are any results to show
   */
  const hasResults = totalResults > 0 || aiResponse;

  /**
   * Reset selection when results change
   */
  useEffect(() => {
    setSelectedIndex(-1);
    itemRefs.current = [];
  }, [query]);

  /**
   * Focus input on mount
   */
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  /**
   * Handle keyboard navigation
   */
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => {
          const newIndex = prev < flatResults.length - 1 ? prev + 1 : 0;
          itemRefs.current[newIndex]?.scrollIntoView({ block: 'nearest' });
          return newIndex;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => {
          const newIndex = prev > 0 ? prev - 1 : flatResults.length - 1;
          itemRefs.current[newIndex]?.scrollIntoView({ block: 'nearest' });
          return newIndex;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < flatResults.length) {
          onResultSelect(flatResults[selectedIndex]);
          onClose();
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  }, [selectedIndex, flatResults, onResultSelect, onClose]);

  /**
   * Handle AI search
   */
  const handleAISearch = useCallback(async () => {
    if (!query.trim() || isAISearching) return;

    setIsAISearching(true);
    setAIResponse(null);

    try {
      const response = await LexAIService.sendChatMessage({
        message: `Search for: ${query}`,
        userId: 'search-user',
        conversationId: `search-${Date.now()}`,
        userName: 'User',
        userEmail: ''
      });

      if (response?.message) {
        setAIResponse(response.message);
      } else {
        setAIResponse('No results found. Try a different search term.');
      }
    } catch (error) {
      console.error('AI search error:', error);
      setAIResponse('Unable to perform AI search at this time.');
    } finally {
      setIsAISearching(false);
    }
  }, [query, isAISearching]);

  /**
   * Get icon for result type
   */
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'client':
        return <User size={16} />;
      case 'deal':
        return <DollarSign size={16} />;
      case 'task':
        return <CheckSquare size={16} />;
      case 'email':
        return <Mail size={16} />;
      default:
        return <Search size={16} />;
    }
  };

  /**
   * Render a group of results
   */
  const renderGroup = (title: string, results: SearchResult[], startIndex: number) => {
    if (results.length === 0) return null;

    return (
      <div className="search-results-group" key={title}>
        <div className="search-results-group-header">
          {title} ({results.length})
        </div>
        {results.map((result, index) => {
          const globalIndex = startIndex + index;
          const isSelected = globalIndex === selectedIndex;

          return (
            <div
              key={result.id}
              ref={el => { itemRefs.current[globalIndex] = el; }}
              className={`search-result-item ${isSelected ? 'selected' : ''}`}
              onClick={() => {
                onResultSelect(result);
                onClose();
              }}
              onMouseEnter={() => setSelectedIndex(globalIndex)}
              role="option"
              aria-selected={isSelected}
            >
              <span className="search-result-icon">
                {getTypeIcon(result.type)}
              </span>
              <div className="search-result-content">
                <div className="search-result-title">{result.title}</div>
                {result.subtitle && (
                  <div className="search-result-subtitle">{result.subtitle}</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Calculate start indices for each group
  const clientsStartIndex = 0;
  const dealsStartIndex = groupedResults.clients.length;
  const tasksStartIndex = dealsStartIndex + groupedResults.deals.length;
  const emailsStartIndex = tasksStartIndex + groupedResults.tasks.length;

  return (
    <div
      className="search-results-container"
      role="combobox"
      aria-expanded={query.length >= MIN_SEARCH_LENGTH}
      aria-haspopup="listbox"
      aria-owns="search-results-listbox"
    >
      {/* Search Input */}
      <div className="search-input-wrapper">
        <Search size={16} className="search-input-icon" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="search-input"
          aria-label="Search"
          aria-autocomplete="list"
          aria-controls="search-results-listbox"
        />
        {query && (
          <button
            onClick={() => onQueryChange('')}
            className="search-clear-btn"
            aria-label="Clear search"
          >
            <X size={14} />
          </button>
        )}
        <button
          onClick={onClose}
          className="search-close-btn"
          aria-label="Close search"
        >
          <X size={16} />
        </button>
      </div>

      {/* Results Dropdown */}
      {query.length >= MIN_SEARCH_LENGTH && (
        <div
          ref={resultsRef}
          id="search-results-listbox"
          className="search-results-dropdown"
          role="listbox"
          aria-label="Search results"
        >
          {totalResults === 0 && !isAISearching && !aiResponse ? (
            <div className="search-no-results">
              <p>No results found for "{query}"</p>
            </div>
          ) : (
            <>
              {renderGroup('Clients', groupedResults.clients, clientsStartIndex)}
              {renderGroup('Deals', groupedResults.deals, dealsStartIndex)}
              {renderGroup('Tasks', groupedResults.tasks, tasksStartIndex)}
              {renderGroup('Emails', groupedResults.emails, emailsStartIndex)}
            </>
          )}

          {/* AI Search Option */}
          <div className="search-ai-section">
            <button
              onClick={handleAISearch}
              disabled={isAISearching}
              className="search-ai-button"
              aria-label="Search with AI"
            >
              {isAISearching ? (
                <>
                  <Loader2 size={16} className="search-ai-spinner" />
                  Searching with AI...
                </>
              ) : (
                <>
                  <Sparkles size={16} />
                  Search with AI...
                </>
              )}
            </button>

            {/* AI Response */}
            {aiResponse && (
              <div className="search-ai-response">
                <div className="search-ai-response-header">
                  <Sparkles size={14} />
                  AI Response
                </div>
                <div className="search-ai-response-content">
                  {aiResponse}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Styles */}
      <style>{`
        .search-results-container {
          position: relative;
          width: 100%;
        }

        .search-input-wrapper {
          display: flex;
          align-items: center;
          background: var(--canvas-bg-primary, #1a1a2e);
          border: 1px solid var(--canvas-border, #2a2a3e);
          border-radius: 8px;
          padding: 0 12px;
          gap: 8px;
        }

        .search-input-icon {
          color: var(--canvas-text-muted, #6b7280);
          flex-shrink: 0;
        }

        .search-input {
          flex: 1;
          background: transparent;
          border: none;
          outline: none;
          color: var(--canvas-text-primary, #e5e7eb);
          font-size: 14px;
          padding: 10px 0;
        }

        .search-input::placeholder {
          color: var(--canvas-text-muted, #6b7280);
        }

        .search-clear-btn,
        .search-close-btn {
          background: transparent;
          border: none;
          color: var(--canvas-text-muted, #6b7280);
          cursor: pointer;
          padding: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          transition: all 0.2s ease;
        }

        .search-clear-btn:hover,
        .search-close-btn:hover {
          color: var(--canvas-text-primary, #e5e7eb);
          background: var(--canvas-bg-secondary, #2a2a3e);
        }

        .search-results-dropdown {
          position: absolute;
          top: calc(100% + 4px);
          left: 0;
          right: 0;
          background: var(--canvas-bg-secondary, #1e1e2e);
          border: 1px solid var(--canvas-border, #2a2a3e);
          border-radius: 8px;
          max-height: 400px;
          overflow-y: auto;
          z-index: 1000;
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
        }

        .search-results-group {
          border-bottom: 1px solid var(--canvas-border, #2a2a3e);
        }

        .search-results-group:last-of-type {
          border-bottom: none;
        }

        .search-results-group-header {
          padding: 8px 12px;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--canvas-text-muted, #6b7280);
          background: var(--canvas-bg-tertiary, #252538);
        }

        .search-result-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          cursor: pointer;
          transition: background 0.15s ease;
        }

        .search-result-item:hover,
        .search-result-item.selected {
          background: var(--canvas-bg-hover, #2a2a4e);
        }

        .search-result-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 28px;
          height: 28px;
          border-radius: 6px;
          background: var(--canvas-bg-tertiary, #252538);
          color: var(--canvas-accent, #7c3aed);
          flex-shrink: 0;
        }

        .search-result-content {
          flex: 1;
          min-width: 0;
        }

        .search-result-title {
          font-size: 14px;
          font-weight: 500;
          color: var(--canvas-text-primary, #e5e7eb);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .search-result-subtitle {
          font-size: 12px;
          color: var(--canvas-text-muted, #6b7280);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          margin-top: 2px;
        }

        .search-no-results {
          padding: 24px 12px;
          text-align: center;
          color: var(--canvas-text-muted, #6b7280);
        }

        .search-no-results p {
          margin: 0;
          font-size: 14px;
        }

        .search-ai-section {
          padding: 8px 12px;
          border-top: 1px solid var(--canvas-border, #2a2a3e);
        }

        .search-ai-button {
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          padding: 10px 12px;
          background: linear-gradient(135deg, var(--canvas-accent, #7c3aed) 0%, #9333ea 100%);
          border: none;
          border-radius: 6px;
          color: white;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .search-ai-button:hover:not(:disabled) {
          opacity: 0.9;
          transform: translateY(-1px);
        }

        .search-ai-button:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .search-ai-spinner {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .search-ai-response {
          margin-top: 12px;
          padding: 12px;
          background: var(--canvas-bg-tertiary, #252538);
          border-radius: 6px;
          border: 1px solid var(--canvas-accent, #7c3aed);
        }

        .search-ai-response-header {
          display: flex;
          align-items: center;
          gap: 6px;
          font-size: 12px;
          font-weight: 600;
          color: var(--canvas-accent, #7c3aed);
          margin-bottom: 8px;
        }

        .search-ai-response-content {
          font-size: 13px;
          color: var(--canvas-text-primary, #e5e7eb);
          line-height: 1.5;
          white-space: pre-wrap;
        }

        /* Scrollbar styling */
        .search-results-dropdown::-webkit-scrollbar {
          width: 6px;
        }

        .search-results-dropdown::-webkit-scrollbar-track {
          background: transparent;
        }

        .search-results-dropdown::-webkit-scrollbar-thumb {
          background: var(--canvas-border, #2a2a3e);
          border-radius: 3px;
        }

        .search-results-dropdown::-webkit-scrollbar-thumb:hover {
          background: var(--canvas-text-muted, #6b7280);
        }
      `}</style>
    </div>
  );
};

export default SearchResults;
